import { useRef, useEffect, useCallback } from "react";
import { buildWsUrl, getWsToken } from "@/lib/api";

// Reconnecting WebSocket: initial 2s backoff, max 15s.
// Fetches a fresh short-lived ws-token from the backend before each connection.
export function useReconnectingSocket({ path, enabled = true, onMessage, onStatus }) {
  const wsRef = useRef(null);
  const delayRef = useRef(2000);
  const timerRef = useRef(null);
  const stoppedRef = useRef(false);
  const msgRef = useRef(onMessage);
  const statusRef = useRef(onStatus);
  msgRef.current = onMessage;
  statusRef.current = onStatus;

  const connect = useCallback(async () => {
    if (stoppedRef.current) return;
    let ws;
    try {
      const token = await getWsToken();
      if (stoppedRef.current) return;
      ws = new WebSocket(buildWsUrl(path, token));
    } catch (e) {
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      delayRef.current = 2000;
      statusRef.current?.(true);
    };
    ws.onmessage = (event) => {
      try {
        msgRef.current?.(JSON.parse(event.data));
      } catch (e) {
        /* ignore */
      }
    };
    ws.onclose = () => {
      statusRef.current?.(false);
      scheduleReconnect();
    };
    ws.onerror = () => {
      try { ws.close(); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const scheduleReconnect = useCallback(() => {
    if (stoppedRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      delayRef.current = Math.min(delayRef.current * 1.6, 15000);
      connect();
    }, delayRef.current);
  }, [connect]);

  useEffect(() => {
    if (!enabled) return;
    stoppedRef.current = false;
    delayRef.current = 2000;
    connect();
    return () => {
      stoppedRef.current = true;
      clearTimeout(timerRef.current);
      try { wsRef.current?.close(); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, enabled]);

  // WEBXR.8D-QA.3 — réabonnement CLIENT immédiat (ferme la socket courante, reconnecte). Aucun effet serveur.
  const reconnect = useCallback(() => {
    if (stoppedRef.current) return;
    clearTimeout(timerRef.current);
    delayRef.current = 2000;
    const old = wsRef.current;
    wsRef.current = null;
    if (old) { old.onclose = null; try { old.close(); } catch (e) {} statusRef.current?.(false); }
    connect();
  }, [connect]);
  return { reconnect };
}
